import axios from "axios";
import { SasFileResult } from "src/types/InspectionUpdateRequest";

const CHUNK_SIZE = 512 * 1024; // 512kB chunks (Azure recommends <= 100MB)

export async function uploadFileToBlob(
  payload: SasFileResult,
  onUploadProgress?: (progress: number, fileName: string) => void
) {
  if (process.env.VITE_NODE_ENV === "cypress") {
    return;
  }
  if (!payload.files?.[0] || !payload.file) {
    throw new Error("Missing SAS URL or file");
  }

  const fileUrl = payload.files[0]; // SAS URL
  const file = payload.file;
  const file_size = file.size;
  const blockIds: string[] = [];
  let offset = 0;
  let blockNum = 0;

  // Send initial progress
  if (onUploadProgress) {
    onUploadProgress(0, payload.fileName || "");
  }

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + CHUNK_SIZE);
    const blockId = btoa(`block-${blockNum.toString().padStart(6, "0")}`); // must be base64

    const blockUrl = `${fileUrl}&comp=block&blockid=${encodeURIComponent(
      blockId
    )}`;

    const resp = await axios.put(blockUrl, chunk, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": "application/octet-stream",
      },
      onUploadProgress: (e) => {
        // Calculate progress more accurately for each chunk
        const chunkProgress = e.total ? e.loaded / e.total : 0;
        const overallProgress =
          (offset + chunkProgress * CHUNK_SIZE) / file_size;

        if (onUploadProgress) {
          onUploadProgress(
            Math.min(overallProgress, 0.99),
            payload.fileName || ""
          );
        }
      },
    });

    blockIds.push(blockId);
    offset += CHUNK_SIZE;
    blockNum++;

    // Send progress update after each chunk is uploaded
    if (onUploadProgress) {
      const progress = Math.min(offset / file_size, 0.99);
      onUploadProgress(progress, payload.fileName || "");
    }
  }

  // Commit the block list
  const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
      <BlockList>
        ${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}
      </BlockList>`;

  const commitUrl = `${fileUrl}&comp=blocklist`;
  await axios.put(commitUrl, blockListXml, {
    headers: {
      "x-ms-blob-content-type": file.type || "application/octet-stream",
    },
  });

  // Send final progress update
  if (onUploadProgress) {
    onUploadProgress(1, payload.fileName || "");
  }
}
